import { FaroSwitch } from "@components/common/faro-switch";
import { AvailableLinks } from "@components/external-links";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { BaseProjectIdProps } from "@custom-types/base-types";
import { useHasUserValidPermissionProjectLevel } from "@hooks/permission-control/use-has-user-valid-permission-project-level";
import { useLanguage } from "@hooks/use-language";
import {
  FeatureSettingLayout,
  TOOLTIP_TEXT_CHECKED,
  TOOLTIP_TEXT_NOT_CHECKED,
} from "@pages/project-details/project-settings/feature-setting-layout";
import { useCompanyId } from "@context-providers/company-id-provider";
import {
  getProjectByIdSelector,
  isFeatureEnabledForProjectSelector,
  isSelectedProjectEditableSelector,
} from "@store/projects/projects-selector";
import { editProjectFeature } from "@store/projects/projects-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { ProjectEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useState } from "react";

/** Renders video mode feature setting */
export function FeatureVideoMode({
  projectId,
}: BaseProjectIdProps): JSX.Element | null {
  const companyId = useCompanyId();
  const { handleErrorWithToast } = useErrorContext();
  const dispatch = useAppDispatch();
  const { trackEvent } = useTrackEvent();
  const { currentLanguage } = useLanguage();

  const selectedProject = useAppSelector(getProjectByIdSelector(projectId));
  const isProjectEditable = useAppSelector(isSelectedProjectEditableSelector());

  const [isLoading, setIsLoading] = useState<boolean>(false);

  /** Determines if the video mode feature is enabled for the selected project */
  const isVideoModeEnabled = useAppSelector(
    isFeatureEnabledForProjectSelector(projectId, "global-videomode")
  );
  const { canEditProjectSettings } = useHasUserValidPermissionProjectLevel({
    project: selectedProject,
  });

  /** Triggers when feature settings toggle button in clicked */
  async function onChangeFeatureSetting(
    shouldEnableImage2d: boolean
  ): Promise<void> {
    // Track event feature toggling
    trackEvent({
      name: ProjectEvents.adjustFeatureSettings,
      props: {
        featureName: "global-videomode",
        isFeatureEnabled: shouldEnableImage2d,
      },
    });

    if (selectedProject) {
      setIsLoading(true);

      try {
        await dispatch(
          editProjectFeature({
            companyId,
            selectedProject,
            payload: {
              ["global-videomode"]: shouldEnableImage2d,
            },
          })
        ).unwrap();
      } catch (error) {
        handleErrorWithToast({
          id: `editVideoModeSetting-${Date.now().toString()}`,
          title: "Error changing video mode setting",
          error,
        });
      } finally {
        setIsLoading(false);
      }
    }
  }

  const actionComponent = (
    <FaroSwitch
      disabled={!isProjectEditable || !canEditProjectSettings}
      checked={isVideoModeEnabled}
      isLoading={isLoading}
      onChange={(_, isChecked) => onChangeFeatureSetting(isChecked)}
      tooltipTextNotChecked={TOOLTIP_TEXT_NOT_CHECKED}
      tooltipTextChecked={TOOLTIP_TEXT_CHECKED}
      sx={{ cursor: !isProjectEditable ? "not-allowed" : "pointer" }}
    />
  );

  return (
    <FeatureSettingLayout
      featureName="VideoMode"
      featureDescription="Enables the use of VideoMode for this project."
      learnMoreUrl={AvailableLinks.videoModeHelp(currentLanguage.code)}
      actionComponent={actionComponent}
      shouldShowDivider
    />
  );
}
